import { ref, computed } from 'vue';
import { api } from '@/services/api';

// Currency mapping - same as backend
const CURRENCY_MAP = {
  USD: { symbol: '$', name: 'US Dollar', position: 'before' },
  ILS: { symbol: '₪', name: 'Israeli Shekel', position: 'before' },
  EUR: { symbol: '€', name: 'Euro', position: 'before' },
  GBP: { symbol: '£', name: 'British Pound', position: 'before' },
  JPY: { symbol: '¥', name: 'Japanese Yen', position: 'before' },
  CAD: { symbol: 'C$', name: 'Canadian Dollar', position: 'before' },
  AUD: { symbol: 'A$', name: 'Australian Dollar', position: 'before' },
  CHF: { symbol: 'Fr', name: 'Swiss Franc', position: 'before' },
  CNY: { symbol: '¥', name: 'Chinese Yuan', position: 'before' },
  INR: { symbol: '₹', name: 'Indian Rupee', position: 'before' }
};

// Shared state for currency - initialized once, module-level so all instances share it
const currentCurrency = ref('USD');
const isLoading = ref(false);
let loadPromise = null; // deduplicate concurrent loads

export function useCurrency() {
  /**
   * Get currency symbol for current or specified currency
   */
  const getCurrencySymbol = (currencyCode = null) => {
    const code = currencyCode || currentCurrency.value;
    return CURRENCY_MAP[code]?.symbol || '$';
  };

  /**
   * Get currency name
   */
  const getCurrencyName = (currencyCode = null) => {
    const code = currencyCode || currentCurrency.value;
    return CURRENCY_MAP[code]?.name || 'US Dollar';
  };

  /**
   * Format amount with currency symbol
   */
  const formatCurrency = (amount, currencyCode = null) => {
    if (amount === null || amount === undefined) return '-';

    const code = currencyCode || currentCurrency.value;
    const currency = CURRENCY_MAP[code] || CURRENCY_MAP.USD;

    const formattedAmount = Number(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

    if (currency.position === 'before') {
      return `${currency.symbol}${formattedAmount}`;
    } else {
      return `${formattedAmount} ${currency.symbol}`;
    }
  };

  /**
   * Format amount for PDF output — uses the ISO currency code instead of the symbol
   * because jsPDF's built-in fonts don't support non-Latin-1 glyphs (₪, €, ₹ etc.)
   */
  const formatCurrencyForPDF = (amount, currencyCode = null) => {
    if (amount === null || amount === undefined) return '-';
    const code = currencyCode || currentCurrency.value;
    const formattedAmount = Number(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `${code} ${formattedAmount}`;
  };

  /**
   * Get all supported currencies
   */
  const getSupportedCurrencies = () => {
    return Object.entries(CURRENCY_MAP).map(([code, info]) => ({
      code,
      ...info
    }));
  };

  /**
   * Load currency from backend (shared endpoint, any authenticated role).
   * Deduplicates concurrent calls — safe to call from multiple components.
   */
  const loadCurrency = () => {
    if (isLoading.value && loadPromise) return loadPromise;
    isLoading.value = true;
    loadPromise = api.get('/settings/currency')
      .then(response => {
        if (response.data?.data?.currency) {
          currentCurrency.value = response.data.data.currency;
        }
      })
      .catch(() => {
        // Silently keep the current value on error
      })
      .finally(() => {
        isLoading.value = false;
        loadPromise = null;
      });
    return loadPromise;
  };

  /**
   * Update currency setting
   */
  const updateCurrency = async (newCurrency) => {
    try {
      isLoading.value = true;
      const response = await api.put('/admin/settings/currency', {
        currency: newCurrency
      });

      if (response.data?.data?.currency) {
        currentCurrency.value = response.data.data.currency;
        return { success: true };
      }
    } catch (error) {
      console.error('Failed to update currency:', error);
      return { success: false, error: error.response?.data?.error || 'Failed to update currency' };
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State
    currentCurrency: computed(() => currentCurrency.value),
    isLoading: computed(() => isLoading.value),

    // Methods
    getCurrencySymbol,
    getCurrencyName,
    formatCurrency,
    formatCurrencyForPDF,
    getSupportedCurrencies,
    loadCurrency,
    updateCurrency
  };
}
